export interface ItemSlipPayload {
  name?: string;
  model: string;
  size: string;
  barcode: string;
}

const NO_SIZE = "-";

/**
 * Build the "Item size" text for a slip from a variation's attributes,
 * e.g. { size: "21", style: "5.5" } -> "21 / Style 5.5".
 */
export const formatSlipSize = (
  attributes?: Record<string, string | null> | null,
): string => {
  if (!attributes) return NO_SIZE;

  const filled = Object.entries(attributes).filter(
    ([, value]) => value !== null && value !== undefined && String(value).trim() !== "",
  ) as [string, string][];
  if (filled.length === 0) return NO_SIZE;

  const size = filled.find(([key]) => key.toLowerCase() === "size")?.[1];
  const others = filled
    .filter(([key]) => key.toLowerCase() !== "size")
    .map(([key, value]) => `${key.charAt(0).toUpperCase()}${key.slice(1)} ${value}`);

  return [size, ...others].filter(Boolean).join(" / ");
};

/**
 * Generate the item slip PDF and open the browser print dialog.
 * Throws if the backend fails, so callers can show an error.
 */
export const printItemSlip = async (payload: ItemSlipPayload): Promise<string> => {
  const resp = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/products/generate-item-slip`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        name: payload.name || payload.model,
        size: payload.size || NO_SIZE,
      }),
    },
  );

  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new Error(body?.error || body?.message || "Failed to generate slip");
  }

  const blob = await resp.blob();
  const url = window.URL.createObjectURL(blob);

  const win = window.open(url);
  if (win) {
    win.onload = () => {
      win.print();
      URL.revokeObjectURL(url);
    };
  }

  return url;
};
